import ReactGA from 'react-ga4'
import TagManager from 'react-gtm-module'

import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'

import {
    IChangePathEventAction,
    IGoogleAnalyticsState,
    ISetCustomEventAction,
    ISetUserEventAction,
} from 'redux/googleAnalytics/googleAnalytics.interface'
import { themeConfig } from 'themes'
import { isProductionEnvironment } from 'utils/common'

const ANALYTICS_TOGGLE_ENABLED = isProductionEnvironment ? themeConfig.ANALYTICS_TOGGLE_ENABLED : true
const GTAG_ID = themeConfig.ANALYTICS_GTAG_ID
const GTM_ID = themeConfig.ANALYTICS_GTM_ID

const initialState: IGoogleAnalyticsState = {
    gtagEnabled: false,
    isInitialPath: true,
    isUserSet: false,
    sharedPayload: null,
}

const googleAnalyticsSlice = createSlice({
    name: 'googleAnalytics',
    initialState,
    reducers: {
        initializeGoogleAnalytics: state => {
            if (ANALYTICS_TOGGLE_ENABLED && !state.gtagEnabled) {
                state.gtagEnabled = true

                const userId = Cookies.get('user_id')

                ReactGA.initialize(GTAG_ID, {
                    gaOptions: {
                        user_id: userId ? userId?.replaceAll('-', '_') : null,
                    },
                })
                // TODO: delete when GA4 will be configured well.
                TagManager.initialize({ gtmId: GTM_ID })
            }
        },
        changePathEvent: (state, action: PayloadAction<IChangePathEventAction>) => {
            if (!state.gtagEnabled && ANALYTICS_TOGGLE_ENABLED) {
                return
            }

            if (state.isInitialPath) {
                state.isInitialPath = false

                return
            }

            const { pathname } = action.payload

            ReactGA.send({ hitType: 'pageview', page: pathname, ...state.sharedPayload })
        },
        setUserEvent: (state, action: PayloadAction<ISetUserEventAction>) => {
            const { identityId, status, kycStatus, userRegistrationIntent } = action.payload

            state.sharedPayload = {
                status,
                kycStatus,
                userRegistrationIntent,
            }

            ReactGA.set({
                user_id: identityId?.replaceAll('-', '_'),
            })

            state.isUserSet = true
        },
        setCustomEvent: (state, action: PayloadAction<ISetCustomEventAction>) => {
            if (!ANALYTICS_TOGGLE_ENABLED || !state.gtagEnabled) {
                return
            }

            const { eventAction, eventCategory, value, kwargs } = action.payload

            ReactGA.send({
                hitType: 'event',
                eventCategory,
                eventAction,
                value,
                ...kwargs,
                ...state.sharedPayload,
            })
        },
    },
})

export const { initializeGoogleAnalytics, changePathEvent, setUserEvent, setCustomEvent } = googleAnalyticsSlice.actions

export default googleAnalyticsSlice.reducer
